import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Upload, Image as ImageIcon, RotateCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface BrandingUploaderProps {
  label: string;
  currentUrl: string | null | undefined;
  acceptedTypes?: string;
  acceptedTypesLabel?: string;
  maxSizeKb?: number;
  recommendedSize?: string;
  onUpload: (file: File) => Promise<string>;
  onReset: () => Promise<void>;
  previewHeight?: string;
}

const BrandingUploader: React.FC<BrandingUploaderProps> = ({
  label,
  currentUrl,
  acceptedTypes = 'image/png, image/jpeg, image/jpg, image/gif, image/svg+xml',
  acceptedTypesLabel = 'PNG, JPG, JPEG, GIF, SVG',
  maxSizeKb = 2048,
  recommendedSize,
  onUpload,
  onReset,
  previewHeight = 'h-40',
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(currentUrl || null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { toast } = useToast();

  React.useEffect(() => {
    setPreview(currentUrl || null);
  }, [currentUrl]);

  const validateFile = (file: File): boolean => {
    setUploadError(null);

    if (!file.type.match('image.*')) {
      setUploadError('Please select an image file');
      return false;
    }

    if (file.size > maxSizeKb * 1024) {
      setUploadError(`Image size should be less than ${maxSizeKb}KB`);
      return false;
    }

    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    setFile(null);

    if (e.target.files && e.target.files.length > 0) {
      const selected = e.target.files[0];
      if (!validateFile(selected)) return;

      setFile(selected);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(selected);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const url = await onUpload(file);
      setPreview(url);
      setFile(null);
      toast({
        title: `${label} uploaded`,
        description: `Your ${label.toLowerCase()} has been updated successfully.`,
      });
    } catch (error: any) {
      console.error(`Error uploading ${label}:`, error);
      setUploadError(error?.message || `Failed to upload ${label.toLowerCase()}. Please try again.`);
      toast({
        title: `Upload failed`,
        description: error?.message || `Failed to upload ${label.toLowerCase()}.`,
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = async () => {
    try {
      await onReset();
      toast({
        title: `${label} reset`,
        description: `Your ${label.toLowerCase()} has been reset to the default.`,
      });
    } catch (error: any) {
      toast({
        title: `Reset failed`,
        description: error?.message || `Failed to reset ${label.toLowerCase()}.`,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      {uploadError && (
        <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{uploadError}</div>
      )}

      <div className={`border rounded-md p-4 flex items-center justify-center bg-gray-50 ${previewHeight}`}>
        {preview ? (
          <img
            src={preview}
            alt={label}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <div className="text-center text-gray-400">
            <ImageIcon className="mx-auto h-12 w-12 mb-2" />
            <p>No {label.toLowerCase()} uploaded</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4">
        <Input
          type="file"
          accept={acceptedTypes}
          onChange={handleFileChange}
          className="mt-2"
        />
        <p className="text-xs text-gray-500">
          Accepted formats: {acceptedTypesLabel}. Maximum size: {maxSizeKb}KB.
          {recommendedSize && ` Recommended size: ${recommendedSize}.`}
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleUpload}
            disabled={!file || isUploading}
            className="w-full sm:w-auto"
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" /> Upload {label}
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={isUploading}
            className="w-full sm:w-auto"
          >
            <RotateCcw className="mr-2 h-4 w-4" /> Reset to Default
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BrandingUploader;
